import React, { forwardRef, useCallback } from 'react';
import { Headline } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import { Card } from './CarlineCard';
import { CargroupCard } from './CargroupCard';
import { CardList } from './CardList';
import type { Carline } from '../utils/filterUtils';
import { NoResults } from './NoResults';
import { ResultCountHeader } from './ResultCountHeader';
import { useContent } from '../context/ContentContext';
import { useFilterContext } from '../context/FilterContext';
import ActiveFilterTags from './button/ActiveFilterTags';
import { useI18n } from '../i18n';
import { useTrackingManager } from '../hooks/useTrackingManager';
import { useUniversalState } from '../context/UniversalStateContext';

const getCardForCarline = (carline: Carline, index: number) => {
  const {
    identifier: { id },
    consumptionAndEmission,
    group,
  } = carline;

  return (
    <Card
      index={index}
      key={`${group}-${id}`}
      consumptionAndEmission={consumptionAndEmission?.summary || undefined}
      carline={carline}
    />
  );
};

export interface ResultsProps {
  readonly resetAllFilters: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const Results = forwardRef<HTMLDivElement, ResultsProps>(({ resetAllFilters }, ref) => {
  const { compareMode } = useContent();
  const {
    setSelectedCarlineGroups,
    selectedVehicleType,
    hasActiveFilters,
    preFilteredCarlineGroups,
    filteredCarlines,
  } = useFilterContext();

  const hasCarlineGroups = preFilteredCarlineGroups.length > 0;
  const hasFilteredCarlines = filteredCarlines.length > 0;
  const isGroupedView = !hasActiveFilters;
  const compareStandaloneHeadline = useI18n('compare.standaloneHeadline');
  const { translations } = useUniversalState();
  const tracking = useTrackingManager();

  const renderResultCountHeader = () =>
    hasCarlineGroups && hasFilteredCarlines ? (
      <ResultCountHeader
        hasActiveFilters={hasActiveFilters}
        selectedVehicleType={selectedVehicleType ?? []}
        filteredCount={filteredCarlines.length}
      />
    ) : null;

  const noResults =
    hasActiveFilters && hasCarlineGroups && !hasFilteredCarlines ? (
      <NoResults onClick={resetAllFilters} />
    ) : null;

  const renderFilteredCards = () => (
    <CardList>
      {filteredCarlines.map((carline, index) => getCardForCarline(carline, index))}
    </CardList>
  );
  // TODO: add Headline to i18n
  const renderCompareStandaloneHeadline = () => (
    <Headline
      variant={{ order: '3', type: 'headline', weight: 'normal' }}
      tag="h3"
      margin={[0, 0, 'var(--spacing-relative-2xl)', 0]}
    >
      {compareStandaloneHeadline}
    </Headline>
  );

  const handleCarGroupClick = useCallback(
    (id: string) => {
      setSelectedCarlineGroups([id]);

      tracking.trackFilterToggle({
        label: translations[`carlineGroup.${id}`],
        kind: 'carlineGroup',
        id,
        next: { vehicleTypes: [], carlineGroups: [id], bodyTypes: [] },
        added: true,
        ui: 'image',
        variant: 'group',
      });
    },
    [tracking, setSelectedCarlineGroups],
  );

  // TODO: pass index for eager loading of first image
  const renderGroupedCards = () => (
    <GroupCardGrid>
      {preFilteredCarlineGroups
        .filter((group) => group.identifier.id !== 'new')
        .map((group) => {
          const firstCarline = group.carlines[0];
          if (!firstCarline) return null;
          const { id } = group.identifier;
          return (
            <CargroupCard
              key={id}
              groupName={group.name}
              groupId={id}
              carline={{ ...firstCarline, group: id }}
              modelCount={group.carlines.length}
              onClick={handleCarGroupClick}
              dataTestId={`cargroupCard-${id}`}
            />
          );
        })}
    </GroupCardGrid>
  );
  return (
    <ResultsRoot ref={ref}>
      {noResults}
      {!noResults && (
        <>
          {compareMode === 'compare-only' && renderCompareStandaloneHeadline()}
          <ActiveFilterTags />
          {renderResultCountHeader()}
          {isGroupedView ? renderGroupedCards() : renderFilteredCards()}
        </>
      )}
    </ResultsRoot>
  );
});

const ResultsRoot = styled.div`
  display: block;
`;

const GroupCardGrid = styled.div`
  display: grid;
  gap: var(--spacing-relative-md);
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  justify-content: flex-start;
`;
